import { useCallback, useMemo, useState, useEffect } from "react";
import {
  SendOutlined,
  SaveOutlined,
  CheckOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Row, Col, Space } from "antd";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { MAX, MIN } from "../formSteps";
import PasswordModal from "../passwordModal";
import { validateProducts } from "../products";
import SmsConfirm, { smsCodeId } from "./smsConfirm";
import IconButton from "../../../app/generic/iconButton";
import SubmitButton from "../../../app/generic/submitButton";
import { error, success } from "../../../../modules/messages";
import { deleteEntity } from "../../../../modules/crudOperations";

const Submit = ({
  id,
  form,
  current,
  loading,
  lastPage,
  onDataSend,
  saveLoading,
  oldPassword,
  successSend,
  onSetCurrent,
  onFormHasError,
  initialContract,
  onTermsHasError,
  onProductsHasError,
}) => {
  const intl = useIntl();

  const [canBeSent, setCanBeSent] = useState();

  const handleBackClick = useCallback(() => {
    onSetCurrent(current - 1);
  }, [current, onSetCurrent]);
  const handleContinueClick = useCallback(() => {
    onSetCurrent(current + 1);
  }, [current, onSetCurrent]);

  const handleValidationClick = useCallback(
    (_, resolve) => {
      const onError = () => {
        resolve && resolve(false);
        error(
          intl.formatMessage({
            id: "registration.invalid",
            defaultMessage: "Formulář není validní!",
          })
        );
      };
      const onSuccess = (values) => {
        onFormHasError();
        onTermsHasError(!values.termsAndConditions);
        const invalidProducts = !validateProducts(values);
        onProductsHasError(invalidProducts);
        //onPersonalDataHasError(!values.personalData);
        if (
          values.termsAndConditions &&
          //values.personalData &&
          !invalidProducts
        ) {
          resolve
            ? resolve(true)
            : success(
                intl.formatMessage({
                  id: "registration.valid",
                  defaultMessage: "Formulář je validní a připraven k odeslání!",
                })
              );
        } else {
          onError();
        }
      };
      form
        .validateFields()
        .then((values) => {
          onSuccess(values);
        })
        .catch((err) => {
          if (
            err?.errorFields?.length === 1 &&
            err.errorFields[0].name[0] === smsCodeId
          ) {
            onSuccess(err.values);
          } else {
            onFormHasError(true);
            onTermsHasError(!err.values.termsAndConditions);
            onProductsHasError(!validateProducts(err.values));
            //onPersonalDataHasError(!err.values.personalData);
            onError();
          }
        });
    },
    [intl, form, onFormHasError, onTermsHasError, onProductsHasError]
  );

  const [openModal, setOpenModal] = useState();
  const [password, setPassword] = useState(oldPassword);
  const handleSaveClick = useCallback(
    (_, current) => {
      current = current ? current : password;
      form.setFieldValue("password", current);
      current
        ? onDataSend({
            ...form.getFieldsValue(true),
            password: current,
          })
        : setOpenModal(true);
    },
    [form, password, onDataSend]
  );
  const handleSetPassword = useCallback(
    (value) => {
      setPassword(value);
      handleSaveClick(undefined, value);
    },
    [handleSaveClick]
  );

  const navigate = useNavigate();
  const [dropping, setDropping] = useState();
  const handleDropSuccess = useCallback(() => {
    setDropping();
    navigate("/form");
  }, [navigate]);
  const handleDropClick = useCallback(() => {
    if (id) {
      const entity = intl.formatMessage({
        id: "registration.old",
        defaultMessage: "Rozpracovaná registrace",
      });
      setDropping(true);
      deleteEntity(
        "contract",
        `${id}?password=${password}`,
        entity,
        entity.toLowerCase(),
        handleDropSuccess,
        setDropping,
        true
      );
    }
  }, [id, intl, password, handleDropSuccess]);

  const disabledOptions = useMemo(
    () => loading || saveLoading || dropping || successSend,
    [loading, dropping, successSend, saveLoading]
  );

  const [run, setRun] = useState();
  useEffect(() => {
    if (!run) {
      initialContract?.bankAccount && handleValidationClick(null, () => {});
      setRun(true);
    }
  }, [initialContract, handleValidationClick, run]);

  return (
    <>
      {openModal && (
        <PasswordModal
          open={openModal}
          onCancel={setOpenModal}
          onSetPassword={handleSetPassword}
        />
      )}
      <SmsConfirm
        id={id}
        form={form}
        show={lastPage}
        onSetCanBeSent={setCanBeSent}
        onValidation={handleValidationClick}
      />
      <Row justify="space-between" gutter={[0, 40]}>
        <Col>
          <SubmitButton
            start
            loading={loading}
            showButton={lastPage}
            icon={<SendOutlined />}
            disabled={disabledOptions || !canBeSent}
            text={
              <FormattedMessage
                id="registration.submit"
                defaultMessage="odeslat registraci"
              />
            }
            title={
              canBeSent ? (
                <FormattedMessage
                  id="registration.submit.title"
                  defaultMessage="odešle registraci do DHL"
                />
              ) : (
                <FormattedMessage
                  id="registration.submit.sms"
                  defaultMessage="pro odeslání registrace je nutné potvrdit telefon"
                />
              )
            }
            cols={
              <>
                {id && (
                  <Col>
                    <IconButton
                      icon={<DeleteOutlined />}
                      onClick={handleDropClick}
                      disabled={disabledOptions}
                      text={
                        <FormattedMessage
                          id="registration.drop"
                          defaultMessage="smazat registraci"
                        />
                      }
                      title={
                        <FormattedMessage
                          id="registration.drop.title"
                          defaultMessage="smaže dříve uloženou registraci"
                        />
                      }
                    />
                  </Col>
                )}
                {current !== MAX && (
                  <Col>
                    <IconButton
                      icon={<CheckOutlined />}
                      disabled={disabledOptions}
                      onClick={handleValidationClick}
                      text={
                        <FormattedMessage
                          id="registration.validate"
                          defaultMessage="validovat formulář"
                        />
                      }
                      title={
                        <FormattedMessage
                          id="registration.validate.title"
                          defaultMessage="provede kontrolu dat ve formuláři"
                        />
                      }
                    />
                  </Col>
                )}
                <Col>
                  <IconButton
                    loading={saveLoading}
                    icon={<SaveOutlined />}
                    onClick={handleSaveClick}
                    disabled={disabledOptions}
                    text={
                      <FormattedMessage
                        id="registration.save"
                        defaultMessage="uložit data"
                      />
                    }
                    title={
                      <FormattedMessage
                        id="registration.save.title"
                        defaultMessage="uloží formulář pro pozdější vyplnění"
                      />
                    }
                  />
                </Col>
              </>
            }
          />
        </Col>
        <Col>
          <Space size={20}>
            {current > MIN && (
              <IconButton
                onClick={handleBackClick}
                text={
                  <FormattedMessage
                    id="registration.back"
                    defaultMessage="zpět"
                  />
                }
              />
            )}
            {current < MAX && (
              <IconButton
                onClick={handleContinueClick}
                text={
                  <FormattedMessage
                    id="registration.continue"
                    defaultMessage="pokračovat"
                  />
                }
              />
            )}
          </Space>
        </Col>
      </Row>
    </>
  );
};

export default Submit;
